import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Trash2, ShieldCheck, Mail, LogIn, ArrowLeft, Info } from "lucide-react";
import { toast } from "sonner";

/**
 * Yol Hattı Şoför – Hesap ve Verileri Silme Talebi
 * Public URL: /hesap-silme
 *
 * Google Play hesap silme gereksinimi. Sayfa herkese açık; kullanıcının
 * hesabını fiilen silmek için OTURUM AÇMIŞ olması zorunludur (mevcut JWT
 * auth). Sadece e-posta yazmak silme için yeterli değildir.
 */
export default function AccountDelete() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [ack1, setAck1] = useState(false);
  const [ack2, setAck2] = useState(false);

  const canSubmit = user && ack1 && ack2 && confirm.trim().toUpperCase() === "SIL" && !busy;

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    // Second, explicit browser confirm — protects against accidental clicks.
    const ok = window.confirm(
      "Hesabınız ve kişisel verileriniz kalıcı olarak silinecek. Bu işlem GERİ ALINAMAZ. Devam edilsin mi?"
    );
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/me/delete-account", { confirmation: "SIL" });
      if (r.data?.deleted) {
        toast.success("Hesabınız silindi. İyi günler dileriz.");
        try { await logout?.(); } catch (_) {}
        setTimeout(() => nav("/", { replace: true }), 1200);
      } else {
        toast.error("Silme tamamlanamadı, lütfen tekrar deneyin.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Silme sırasında bir hata oluştu.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main data-testid="account-delete-page" className="min-h-screen bg-white text-neutral-900">
      {/* Hero */}
      <section className="bg-[hsl(var(--brand-black))] text-white">
        <div className="mx-auto max-w-3xl px-5 py-14 sm:py-16">
          <div className="inline-flex items-center gap-2 text-xs font-bold px-3 py-1 rounded-full bg-primary text-[hsl(var(--brand-black))] mb-5">
            <Trash2 className="w-3.5 h-3.5" /> Hesap Silme
          </div>
          <h1
            data-testid="account-delete-title"
            className="text-4xl sm:text-5xl font-black tracking-tight leading-tight"
          >
            Yol Hattı Şoför —{" "}
            <span className="text-primary bg-[hsl(var(--brand-black))]">Hesap ve Verileri Silme Talebi</span>
          </h1>
          <p className="mt-4 text-sm text-neutral-300">
            Bu sayfa Google Play gereksinimlerine uygun olarak, Yol Hattı Şoför
            uygulaması ve <b>yolhatti.com</b> hesap sahiplerinin kendi
            hesaplarını güvenli şekilde silebilmesi içindir.
          </p>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-5 py-10 sm:py-14 space-y-10">
        {/* Silinecek veri özeti */}
        <section data-testid="delete-scope-section">
          <h2 className="text-lg font-black text-[hsl(var(--brand-black))] mb-3">
            Hesabınız silindiğinde ne olur?
          </h2>
          <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-5 text-[15px] leading-relaxed space-y-3">
            <div className="flex items-start gap-2">
              <span className="mt-1 w-2 h-2 rounded-full bg-primary shrink-0" />
              <p>
                <b>Silinen kişisel veriler:</b> ad–soyad, e-posta adresi, telefon
                numarası, parola özeti, (şoför hesabıysa) atanmış araç bağlantısı
                ve bildirim (FCM push) tokenlarınız.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="mt-1 w-2 h-2 rounded-full bg-primary shrink-0" />
              <p>
                <b>Silinen operasyonel veriler:</b> Yol Hattı üzerindeki
                bildirimleriniz ve favorileriniz kaldırılır. Aktif oturumunuz
                sonlandırılır.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="mt-1 w-2 h-2 rounded-full bg-primary shrink-0" />
              <p>
                <b>Anonimleştirilen kayıtlar (muhasebe/hukuki gereklilik):</b>{" "}
                Geçmiş yol yardım ve taşıma işleriniz silinmez; yalnızca
                bunlardaki kimlik referansınız anonim bir etiketle değiştirilir.
                Böylece firma tarafındaki tamamlanmış görev, ödeme ve fatura
                kayıtları tutarlı kalır ancak size kişisel olarak bağlanamaz.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="mt-1 w-2 h-2 rounded-full bg-primary shrink-0" />
              <p className="text-neutral-700">
                Silme işlemi anında sunucuda uygulanır. E-posta ile yedeklenmiş
                kişisel bir kopya oluşturulmaz.
              </p>
            </div>
          </div>
        </section>

        {/* Yasal saklama notu — dürüst dil */}
        <section data-testid="legal-retention-section">
          <h2 className="text-lg font-black text-[hsl(var(--brand-black))] mb-3">
            Yasal saklama zorunluluğu var mı?
          </h2>
          <div className="rounded-2xl border border-neutral-200 bg-white p-5 text-[15px] leading-relaxed">
            <p>
              Yürürlükteki mevzuat (ör. Türk Ticaret Kanunu, Vergi Usul Kanunu
              kapsamındaki defter ve belge saklama yükümlülükleri) gereği
              tamamlanmış işlere ait <b>işlem kayıtları</b>, <b>fatura/ödeme
              dekont bilgileri</b> ve benzeri finansal kayıtların kanunda
              belirtilen süre boyunca tutulması zorunlu olabilir. Bu gibi
              zorunlu kayıtlarda kişisel kimliğiniz yukarıda açıklandığı üzere
              anonimleştirilir; kaydın kendisi mali/hukuki gereklilik ortadan
              kalkana kadar saklanabilir.
            </p>
            <p className="mt-3 text-neutral-700">
              Yol Hattı, kanunun öngörmediği bir saklama süresi ilan etmez;
              hesabınıza bağlı kişisel veriler yukarıdaki listeye uygun olarak
              anında silinir.
            </p>
          </div>
        </section>

        {/* Uygun değilse yönlendirmeler */}
        {!user && (
          <section data-testid="delete-guest-section">
            <h2 className="text-lg font-black text-[hsl(var(--brand-black))] mb-3">
              Hesabınızı silmek için lütfen giriş yapın
            </h2>
            <div className="rounded-2xl border-2 border-primary bg-primary/5 p-5">
              <p className="text-[15px]">
                Güvenlik gereği, bir başkasının sadece e-posta adresinizi bilerek
                hesabınızı sildirememesi için silme talebi <b>oturum açmış</b>{" "}
                kullanıcıdan gelmelidir.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  data-testid="delete-login-btn"
                  onClick={() => nav("/giris")}
                  className="bg-[hsl(var(--brand-black))] hover:bg-[hsl(var(--brand-black))]/90 text-white"
                >
                  <LogIn className="w-4 h-4 mr-1" /> Giriş yap ve hesabımı sil
                </Button>
                <a
                  data-testid="delete-support-email"
                  href="mailto:yolhattitr@gmail.com?subject=Hesap%20Silme%20Talebi"
                  className="inline-flex items-center rounded-md border border-neutral-300 px-3 h-10 text-sm font-semibold hover:bg-neutral-50"
                >
                  <Mail className="w-4 h-4 mr-1" /> yolhattitr@gmail.com
                </a>
              </div>
              <p className="mt-3 text-xs text-neutral-600 flex items-start gap-1">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                E-posta ile başvurduğunuzda kimliğinizi doğrulamak için hesabınıza
                bağlı e-posta adresini kullanmanız gerekir.
              </p>
            </div>
          </section>
        )}

        {/* Auth'lu silme formu */}
        {user && (
          <section data-testid="delete-form-section">
            <h2 className="text-lg font-black text-[hsl(var(--brand-black))] mb-3">
              Hesabımı sil
            </h2>
            <form onSubmit={submit} className="rounded-2xl border-2 border-red-500 bg-red-50/60 p-5 space-y-4">
              <div className="flex items-start gap-3 text-sm">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-red-800">
                    Bu işlem GERİ ALINAMAZ.
                  </p>
                  <p className="text-neutral-700 mt-1">
                    Oturum:{" "}
                    <span className="font-semibold">{user?.email}</span>{" "}
                    <span className="text-neutral-500">
                      (rol: {user?.role})
                    </span>
                  </p>
                </div>
              </div>

              {user?.role === "business_owner" && (
                <div className="rounded-lg bg-yellow-50 border border-yellow-300 p-3 text-sm">
                  <b>İşletme sahibi hesabı otomatik silinemez.</b> İşletmeniz,
                  araçlar, şoförler ve ödeme geçmişi ile ilişkili olduğu için
                  talebinizi{" "}
                  <a
                    href="mailto:yolhattitr@gmail.com?subject=Isletme%20Hesap%20Silme%20Talebi"
                    className="font-semibold underline"
                  >
                    yolhattitr@gmail.com
                  </a>{" "}
                  adresine hesap e-postanızdan yazın; ekibimiz manuel olarak
                  işleyecektir.
                </div>
              )}

              {user?.role === "admin" && (
                <div className="rounded-lg bg-yellow-50 border border-yellow-300 p-3 text-sm">
                  Yönetici hesabı bu sayfadan silinemez.
                </div>
              )}

              {(user?.role === "user" || user?.role === "driver") && (
                <>
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      data-testid="delete-ack1"
                      type="checkbox"
                      checked={ack1}
                      onChange={(e) => setAck1(e.target.checked)}
                      className="mt-1"
                    />
                    <span>
                      Hesabımın kalıcı olarak silineceğini ve yukarıda listelenen
                      kişisel verilerimin sunucudan kaldırılacağını anladım.
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      data-testid="delete-ack2"
                      type="checkbox"
                      checked={ack2}
                      onChange={(e) => setAck2(e.target.checked)}
                      className="mt-1"
                    />
                    <span>
                      Yasal saklama zorunluluğu bulunan tamamlanmış iş
                      kayıtlarında kimliğimin anonimleştirileceğini kabul
                      ediyorum.
                    </span>
                  </label>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-neutral-700">
                      Onaylamak için aşağıya <b>SIL</b> yazın (büyük harflerle):
                    </label>
                    <Input
                      data-testid="delete-confirm-input"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="SIL"
                      autoComplete="off"
                    />
                  </div>

                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                      data-testid="delete-submit-btn"
                      type="submit"
                      disabled={!canSubmit}
                      className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      {busy ? "Siliniyor…" : "Hesabımı Kalıcı Olarak Sil"}
                    </Button>
                    <Link
                      to="/"
                      className="inline-flex items-center rounded-md border border-neutral-300 px-3 h-10 text-sm font-semibold hover:bg-neutral-50"
                    >
                      Vazgeç
                    </Link>
                  </div>
                </>
              )}
            </form>
          </section>
        )}

        {/* Süreç */}
        <section data-testid="delete-process-section">
          <h2 className="text-lg font-black text-[hsl(var(--brand-black))] mb-3">
            Talebiniz nasıl sonuçlanır?
          </h2>
          <ol className="list-decimal pl-6 space-y-2 text-[15px]">
            <li>
              Yukarıdaki onayı verdiğinizde silme isteği sunucuya iletilir ve{" "}
              <b>anında uygulanır</b>.
            </li>
            <li>
              Kişisel verileriniz ve bildirim tokenlarınız hesap belgeniz ile
              birlikte silinir; oturum çerezleriniz sonlandırılır.
            </li>
            <li>
              Yalnızca mali/hukuki gereklilik taşıyan tamamlanmış iş kayıtları
              anonimleştirilerek muhafaza edilir; bunlar size bağlanamaz.
            </li>
            <li>
              Aktif bir işiniz veya ödeme sürecindeki bir talebiniz varsa silme
              reddedilir; önce süreci tamamlayın veya iptal edin, ardından
              yeniden deneyin.
            </li>
            <li>
              Herhangi bir sorunuz olursa{" "}
              <a
                href="mailto:yolhattitr@gmail.com"
                className="text-primary underline underline-offset-4 font-semibold"
              >
                yolhattitr@gmail.com
              </a>{" "}
              adresine yazabilirsiniz.
            </li>
          </ol>
        </section>

        <div className="pt-4 border-t border-neutral-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-neutral-500">
          <span>© {new Date().getFullYear()} Yol Hattı — Tüm hakları saklıdır.</span>
          <div className="flex items-center gap-3">
            <Link
              to="/gizlilik-politikasi"
              data-testid="delete-privacy-link"
              className="text-[hsl(var(--brand-black))] font-semibold hover:text-primary inline-flex items-center gap-1"
            >
              <ShieldCheck className="w-4 h-4" /> Gizlilik Politikası
            </Link>
            <Link
              to="/"
              className="text-[hsl(var(--brand-black))] font-semibold hover:text-primary inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" /> Ana sayfa
            </Link>
          </div>
        </div>
      </article>
    </main>
  );
}
